import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Search, FileText, Package, Receipt, MapPin, Ship, Calendar, Users, CheckSquare, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import api from '@/services/api'

interface GlobalSearchProps {
  isOpen: boolean
  onClose: () => void
}

interface SearchResult {
  id: number | string
  type: 'bl' | 'container' | 'entity' | 'port' | 'task'
  title: string
  subtitle?: string
  path: string
}

const quickLinks = [
  { icon: FileText, label: 'Bills of Lading', path: '/bl' },
  { icon: Package, label: 'Containers', path: '/containers' },
  { icon: Receipt, label: 'Invoices', path: '/invoices' },
  { icon: MapPin, label: 'Ports', path: '/ports' },
  { icon: Ship, label: 'Vessels', path: '/vessels' },
  { icon: Calendar, label: 'Bookings', path: '/bookings' },
  { icon: Users, label: 'Entities', path: '/entities' },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
]

const getIconForType = (type: string) => {
  switch (type) {
    case 'bl': return FileText
    case 'container': return Package
    case 'entity': return Users
    case 'port': return MapPin
    case 'task': return CheckSquare
    default: return FileText
  }
}

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'bl': return 'Bill of Lading'
    case 'container': return 'Container'
    case 'entity': return 'Entity'
    case 'port': return 'Port'
    case 'task': return 'Task'
    default: return type
  }
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([])
      return
    }

    setIsLoading(true)
    try {
      const response = await api.globalSearch(searchQuery)
      
      if (response.success && response.data) {
        const searchResults: SearchResult[] = []
        
        response.data.bills_of_lading?.forEach((bl: any) => {
          searchResults.push({
            id: bl.id,
            type: 'bl',
            title: bl.hbl || bl.file_no || `BL #${bl.id}`,
            subtitle: bl.mbl ? `MBL: ${bl.mbl}` : undefined,
            path: `/bl/${bl.id}`
          })
        })
        
        response.data.containers?.forEach((container: any) => {
          searchResults.push({
            id: container.id,
            type: 'container',
            title: container.cntr_no || `Container #${container.id}`,
            subtitle: container.seal_no ? `Seal: ${container.seal_no}` : undefined,
            path: `/containers/${container.id}`
          })
        })
        
        response.data.entities?.forEach((entity: any) => {
          searchResults.push({
            id: entity.id,
            type: 'entity',
            title: entity.entity_name || `Entity #${entity.id}`,
            subtitle: entity.email || entity.phone,
            path: `/entities`
          })
        })
        
        response.data.ports?.forEach((port: any) => {
          searchResults.push({
            id: port.id,
            type: 'port',
            title: port.port_name || port.des || `Port #${port.id}`,
            subtitle: port.un_code || port.country,
            path: `/ports/${port.id}`
          })
        })
        
        response.data.tasks?.forEach((task: any) => {
          searchResults.push({
            id: task.id,
            type: 'task',
            title: task.title || task.task_title || `Task #${task.id}`,
            subtitle: task.due_date ? `Due: ${new Date(task.due_date).toLocaleDateString()}` : undefined,
            path: `/tasks`
          })
        })
        
        setResults(searchResults)
      }
    } catch (error) {
      console.error('Search error:', error)
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query) {
        performSearch(query)
      } else {
        setResults([])
      }
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [query, performSearch])

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setResults([])
    }
  }, [isOpen])

  const handleNavigate = (path: string) => {
    navigate(path)
    onClose()
    setQuery('')
    setResults([])
  }

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = []
    }
    acc[result.type].push(result)
    return acc
  }, {} as Record<string, SearchResult[]>)

  return (
    <Dialog open={isOpen} onClose={onClose} title="Global Search" size="xl">
      <div>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search B/Ls, containers, entities, ports, tasks..."
              className="pl-10"
              autoFocus
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground animate-spin" />
            )}
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {query.length >= 2 ? (
            isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-4">
                {Object.entries(groupedResults).map(([type, items]) => (
                  <div key={type}>
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase">
                      {getTypeLabel(type)}s ({items.length})
                    </div>
                    <div className="space-y-1">
                      {items.slice(0, 5).map((result) => {
                        const Icon = getIconForType(result.type)
                        return (
                          <button
                            key={`${result.type}-${result.id}`}
                            onClick={() => handleNavigate(result.path)}
                            className={cn(
                              "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                              "hover:bg-accent focus:bg-accent focus:outline-none"
                            )}
                          >
                            <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="truncate font-medium">{result.title}</div>
                              {result.subtitle && (
                                <div className="truncate text-xs text-muted-foreground">
                                  {result.subtitle}
                                </div>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No results found for "{query}"
              </div>
            )
          ) : query.length > 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search
            </div>
          ) : (
            <div className="space-y-2">
              <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                Quick Links
              </div>
              <div className="grid grid-cols-2 gap-2">
                {quickLinks.map((link) => (
                  <button
                    key={link.path}
                    onClick={() => handleNavigate(link.path)}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                      "hover:bg-accent focus:bg-accent focus:outline-none"
                    )}
                  >
                    <link.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{link.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t p-3 text-xs text-muted-foreground">
          <div className="flex items-center justify-between">
            <span>Press ESC to close • Ctrl+K to open</span>
            <span>{results.length} results</span>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
